import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users } from 'lucide-react';
import { authClient } from '../lib/authClient';
import { getPendingInvite, setPendingInvite, clearPendingInvite } from '../utils/pendingInvite';

// Invite links look like /join#<token>. The token is in the fragment so it never reaches the
// server in a URL (or its access logs); it's kept in localStorage while the person signs in
// or creates an account, and AuthSync brings them back here afterwards.

const ROLE_TEXT = {
  admin: "You'll be able to manage the band's songs, gigs and set lists.",
  performer: "You'll be able to see the band's songs, lyrics, chords and set lists, and mark requests played on stage.",
};

const readStored = getPendingInvite;
const store = setPendingInvite;
const forget = clearPendingInvite;

function takeToken() {
  const fromHash = window.location.hash.replace(/^#/, '').trim();
  if (fromHash) {
    store(fromHash);
    window.history.replaceState(null, '', '/join');  // don't leave the token in the address bar
    return fromHash;
  }
  return readStored();
}

export default function JoinBand() {
  const { data: session, isPending } = authClient.useSession();
  const [token] = useState(takeToken);
  const [invite, setInvite] = useState(null);     // { band_name, role }
  const [status, setStatus] = useState('loading'); // loading | ready | invalid | inBand | joining
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    axios.post('/api/invites/preview', { token })
      .then(res => { setInvite(res.data); setStatus('ready'); })
      .catch(err => {
        setError(err.response?.data?.error || '');
        setStatus('invalid');
        if (err.response?.status === 404) forget();
      });
  }, [token]);

  // Signed in already and in a band? One login can be in one band for now.
  useEffect(() => {
    if (!session?.user || status !== 'ready') return;
    axios.get('/api/me')
      .then(res => { if (res.data.artistId) { forget(); setStatus('inBand'); } })
      .catch(() => {});
  }, [session?.user, status]);

  const join = async () => {
    setStatus('joining');
    setError('');
    try {
      await axios.post('/api/invites/accept', { token });
      forget();
      window.location.assign('/dashboard');  // full reload so the app picks up the new band
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
      if (err.response?.status === 409) { forget(); setStatus('inBand'); }
      else if (err.response?.status === 404) { forget(); setStatus('invalid'); }
      else setStatus('ready');
    }
  };

  const signedIn = !!session?.user;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={styles.icon}><Users size={28} color="#667eea" /></div>
          {status === 'loading' && <p style={styles.muted}>Checking your invite…</p>}

          {status === 'invalid' && (
            <>
              <h1 style={styles.title}>Invite link not valid</h1>
              <p style={styles.muted}>
                {error || "This invite link isn't valid any more — ask the band for a new one."}
              </p>
            </>
          )}

          {(status === 'ready' || status === 'joining') && invite && (
            <>
              <h1 style={styles.title}>Join {invite.band_name}</h1>
              <p style={styles.muted}>
                You've been invited as {invite.role === 'admin' ? 'an Admin' : 'a Performer'}. {ROLE_TEXT[invite.role]}
              </p>
            </>
          )}

          {status === 'inBand' && (
            <>
              <h1 style={styles.title}>You're already in a band</h1>
              <p style={styles.muted}>
                One login can be in one band for now. To join {invite?.band_name || 'this band'}, sign out and
                create a new account with a different email address.
              </p>
            </>
          )}
        </div>

        {error && status === 'ready' && <p style={styles.error}>{error}</p>}

        {(status === 'ready' || status === 'joining') && !isPending && (
          signedIn ? (
            <button onClick={join} disabled={status === 'joining'} style={styles.primary}>
              {status === 'joining' ? 'Joining…' : `Join ${invite.band_name}`}
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link to="/sign-up" style={styles.primary}>Create an account</Link>
              <Link to="/sign-in" style={styles.secondary}>I already have an account</Link>
              <p style={{ ...styles.muted, fontSize: 12, marginTop: 4 }}>
                You'll come back here to finish joining once you're signed in.
              </p>
            </div>
          )
        )}

        {(status === 'invalid' || status === 'inBand') && (
          <Link to={signedIn ? '/dashboard' : '/'} style={styles.secondary}>
            {signedIn ? 'Go to dashboard' : 'Go to home page'}
          </Link>
        )}
      </div>
    </div>
  );
}

const button = {
  display: 'block', width: '100%', padding: 12, borderRadius: 8, fontSize: 15, fontWeight: 600,
  textAlign: 'center', textDecoration: 'none', cursor: 'pointer', boxSizing: 'border-box',
};
const styles = {
  page: {
    minHeight: '100vh', background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a3e 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: '100%', maxWidth: 440, background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: '40px 36px', boxSizing: 'border-box',
  },
  icon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56,
    background: 'rgba(102,126,234,0.2)', borderRadius: 14, marginBottom: 16,
  },
  title: { margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#fff' },
  muted: { margin: 0, fontSize: 14, lineHeight: 1.5, color: 'rgba(255,255,255,0.6)' },
  error: { margin: '0 0 16px', fontSize: 13, color: '#dc3545', textAlign: 'center' },
  primary: { ...button, border: 'none', color: '#fff', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  secondary: { ...button, border: '1px solid rgba(255,255,255,0.2)', color: '#fff', background: 'transparent', marginTop: 4 },
};
